import { useEffect, useRef, useState, type DragEvent } from "react";
import clsx from "clsx";
import { AlertTriangle, ChevronDown, Clock, Maximize2, Minimize2, Minus, MoreHorizontal, Paperclip, Send, Trash2, X } from "lucide-react";
import { Button, Dialog, IconButton, Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from "@dfws/ui";
import { ATTACHMENT_MAX_BYTES, externalRecipients, type Draft, type DraftInput } from "@dfws/contracts";
import { api, ApiFailure } from "../../lib/api";
import { useSession } from "../../lib/queries";
import { formatSize } from "../chat/format";
import { Editor, type EditorHandle } from "./Editor";
import { RecipientField } from "./RecipientField";
import { discardDraft, hideComposer, patchComposer, saveDraft, sendDraft, toast, uploadAttachment, useDraft, useIdentities, useSignatures } from "./composeData";
import s from "./Compose.module.css";

type Addr = { name: string | null; email: string };

const MANY = 10;
const ATTACH_WORDS = /\b(attached|attachment|attaching|enclosed|find the file|see the file)\b/i;

function nextMorning(days: number, weekday?: number): Date {
  const d = new Date();
  d.setDate(d.getDate() + days);
  if (weekday !== undefined) while (d.getDay() !== weekday) d.setDate(d.getDate() + 1);
  d.setHours(8, 0, 0, 0);
  return d;
}

export function ComposeWindow({ draftId, variant, minimized = false, maximized = false }: { draftId: number; variant: "dock" | "inline" | "sheet"; minimized?: boolean; maximized?: boolean }) {
  const q = useDraft(draftId);
  if (!q.data) return <section className={clsx(s.window, s[variant])} aria-busy="true" aria-label="Loading draft" />;
  return <Composer draft={q.data} variant={variant} minimized={minimized} maximized={maximized} />;
}

function Composer({ draft, variant, minimized, maximized }: { draft: Draft; variant: "dock" | "inline" | "sheet"; minimized: boolean; maximized: boolean }) {
  const me = (useSession().data?.user.email ?? "").toLowerCase();
  const myDomain = me.split("@")[1] ?? "";
  const identities = useIdentities();
  const signatures = useSignatures();
  const [to, setTo] = useState<Addr[]>(draft.to);
  const [cc, setCc] = useState<Addr[]>(draft.cc);
  const [bcc, setBcc] = useState<Addr[]>(draft.bcc);
  const [showCc, setShowCc] = useState(draft.cc.length > 0 || draft.bcc.length > 0);
  const [subject, setSubject] = useState(draft.subject);
  const [html, setHtml] = useState(draft.html);
  const [from, setFrom] = useState(draft.from);
  const [signatureId, setSignatureId] = useState<number | null>(draft.signatureId);
  const [attachments, setAttachments] = useState(draft.attachments);
  const [uploads, setUploads] = useState<{ key: string; name: string; progress: number; error?: string }[]>([]);
  const [showQuote, setShowQuote] = useState(false);
  const [saved, setSaved] = useState<"saved" | "saving" | "error">("saved");
  const [confirm, setConfirm] = useState<null | { text: string; go: () => void }>(null);
  const [pickTime, setPickTime] = useState(false);
  const [sending, setSending] = useState(false);
  const [dragging, setDragging] = useState(false);
  const editor = useRef<EditorHandle>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const pending = useRef<DraftInput>({});
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Autosave: collect changes and write them shortly after typing stops.
  const queue = (patch: DraftInput) => {
    pending.current = { ...pending.current, ...patch };
    setSaved("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => void flush(), 800);
  };
  const flush = async () => {
    clearTimeout(timer.current);
    const patch = pending.current;
    pending.current = {};
    if (!Object.keys(patch).length) return;
    try {
      await saveDraft(draft.id, patch);
      setSaved("saved");
    } catch {
      setSaved("error");
      pending.current = { ...patch, ...pending.current };
    }
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  const everyone = [...to, ...cc, ...bcc];
  const external = externalRecipients(everyone, myDomain);
  const signature = signatures.data?.items.find((x) => x.id === signatureId) ?? null;
  const title = subject || (draft.mode === "forward" ? "Forward" : draft.mode === "new" ? "New message" : "Reply");

  const attach = (files: File[]) => {
    for (const file of files) {
      const key = `${file.name}-${Math.random().toString(36).slice(2)}`;
      if (file.size > ATTACHMENT_MAX_BYTES) {
        setUploads((u) => [...u, { key, name: file.name, progress: 0, error: "Larger than 25 MB" }]);
        continue;
      }
      setUploads((u) => [...u, { key, name: file.name, progress: 0 }]);
      uploadAttachment(file, (p) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, progress: p } : x))))
        .then((f) => {
          setUploads((u) => u.filter((x) => x.key !== key));
          setAttachments((a) => {
            const next = [...a, f];
            queue({ attachmentIds: next.map((x) => x.id) });
            return next;
          });
        })
        .catch((err: unknown) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, error: err instanceof ApiFailure ? err.message : "Upload failed" } : x))));
    }
  };

  const send = async (at?: Date) => {
    if (!everyone.length) return toast({ text: "Add at least one recipient.", tone: "error" });
    if (uploads.some((u) => !u.error)) return toast({ text: "Wait for the attachments to finish uploading.", tone: "error" });
    const checks: string[] = [];
    if (!subject.trim()) checks.push("There is no subject.");
    if (!attachments.length && ATTACH_WORDS.test(html.replace(/<[^>]+>/g, " "))) checks.push("You mention an attachment, but nothing is attached.");
    const go = async () => {
      setSending(true);
      try {
        await flush();
        await sendDraft(draft.id, at);
      } catch (err) {
        toast({ text: err instanceof ApiFailure ? err.message : "The message could not be sent.", tone: "error" });
        setSending(false);
      }
    };
    if (checks.length) setConfirm({ text: checks.join(" "), go: () => void go() });
    else await go();
  };

  const close = async () => {
    await flush();
    hideComposer(draft.id);
    const empty = !everyone.length && !subject && !html && !attachments.length;
    if (empty && draft.mode === "new") return void discardDraft(draft.id);
    void api(`/drafts/${draft.id}/checkpoint`, { method: "POST" }).catch(() => {});
    toast({ text: "Draft saved" }, 3000);
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length) attach([...e.dataTransfer.files]);
  };

  const replyTitle = draft.mode === "reply" ? "Reply" : draft.mode === "replyAll" ? "Reply all" : draft.mode === "forward" ? "Forward" : "New message";

  return (
    <section
      className={clsx(s.window, s[variant], minimized && s.min, maximized && s.max)}
      aria-label={`${replyTitle}: ${subject || "no subject"}`}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes("Files")) {
          e.preventDefault();
          setDragging(true);
        }
      }}
      onDragLeave={(e) => e.currentTarget === e.target && setDragging(false)}
      onDrop={onDrop}
      onKeyDown={(e) => {
        if (e.key === "Escape" && variant !== "inline" && !confirm && !pickTime) {
          e.stopPropagation();
          void close();
        }
      }}
    >
      {variant !== "inline" && (
        <header className={s.head} onClick={minimized ? () => patchComposer(draft.id, { minimized: false }) : undefined}>
          <h2 className={s.title}>{title}</h2>
          <div className={s.headActions} onClick={(e) => e.stopPropagation()}>
            {variant === "dock" && <IconButton label={minimized ? "Expand" : "Minimize"} icon={<Minus />} size="sm" onClick={() => patchComposer(draft.id, { minimized: !minimized })} />}
            {variant === "dock" && !minimized && <IconButton label={maximized ? "Exit full size" : "Full size"} icon={maximized ? <Minimize2 /> : <Maximize2 />} size="sm" onClick={() => patchComposer(draft.id, { maximized: !maximized })} />}
            <IconButton label="Save and close" shortcut="Esc" icon={<X />} size="sm" onClick={() => void close()} />
          </div>
        </header>
      )}
      {!minimized && (
        <>
          <div className={s.fields}>
            {(identities.data?.items.length ?? 0) > 1 && (
              <label className={s.field}>
                <span className={s.fieldLabel}>From</span>
                <select className={s.select} value={from} onChange={(e) => (setFrom(e.target.value), queue({ from: e.target.value }))}>
                  {identities.data!.items.map((x) => (
                    <option key={x} value={x}>{x}</option>
                  ))}
                </select>
              </label>
            )}
            <div className={s.toRow}>
              <RecipientField label="To" value={to} onChange={(v) => (setTo(v), queue({ to: v }))} myDomain={myDomain} autoFocus={draft.mode === "new" || draft.mode === "forward"} />
              {!showCc && (
                <button type="button" className={s.ccToggle} onClick={() => setShowCc(true)}>
                  Cc Bcc
                </button>
              )}
            </div>
            {showCc && <RecipientField label="Cc" value={cc} onChange={(v) => (setCc(v), queue({ cc: v }))} myDomain={myDomain} />}
            {showCc && <RecipientField label="Bcc" value={bcc} onChange={(v) => (setBcc(v), queue({ bcc: v }))} myDomain={myDomain} />}
            <label className={s.field}>
              <span className="df-visually-hidden">Subject</span>
              <input className={s.subject} placeholder="Subject" value={subject} maxLength={998} onChange={(e) => (setSubject(e.target.value), queue({ subject: e.target.value }))} />
            </label>
          </div>

          {(external.length > 0 || everyone.length >= MANY) && (
            <div className={s.warn} role="status">
              <AlertTriangle aria-hidden />
              <span>
                {everyone.length >= MANY && <>This goes to {everyone.length} people. </>}
                {external.length > 0 && (
                  <>
                    {external.length === 1 ? "1 person is" : `${external.length} people are`} outside {myDomain}: {external.slice(0, 3).map((a) => a.email).join(", ")}
                    {external.length > 3 ? ` and ${external.length - 3} more` : ""}.
                  </>
                )}
              </span>
            </div>
          )}

          <div className={s.body}>
            <Editor
              ref={editor}
              html={html}
              label="Message body"
              placeholder={draft.mode === "new" ? "Write your message" : "Write your reply"}
              onChange={(h) => (setHtml(h), queue({ html: h }))}
              onSubmit={() => void send()}
              autoFocus={draft.mode === "reply" || draft.mode === "replyAll"}
            />
            {signature && (
              <div className={s.signature} aria-label="Signature">
                {/* Signatures are sanitized on the server when saved. */}
                <div dangerouslySetInnerHTML={{ __html: signature.html }} />
              </div>
            )}
            {draft.quoteHtml && (
              <div className={s.quoteBox}>
                <button type="button" className={s.quoteToggle} aria-expanded={showQuote} onClick={() => setShowQuote((v) => !v)} title={showQuote ? "Hide quoted text" : "Show quoted text"}>
                  <MoreHorizontal />
                </button>
                {showQuote && <div className={s.quote} dangerouslySetInnerHTML={{ __html: draft.quoteHtml }} />}
              </div>
            )}
            {(attachments.length > 0 || uploads.length > 0) && (
              <ul className={s.files} aria-label="Attachments">
                {attachments.map((a) => (
                  <li key={a.id} className={s.file}>
                    <Paperclip aria-hidden />
                    <span className={s.fileName}>{a.name}</span>
                    <span className={s.fileSize}>{formatSize(a.size)}</span>
                    <IconButton label={`Remove ${a.name}`} icon={<X />} size="sm" onClick={() => setAttachments((list) => {
                      const next = list.filter((x) => x.id !== a.id);
                      queue({ attachmentIds: next.map((x) => x.id) });
                      return next;
                    })} />
                  </li>
                ))}
                {uploads.map((u) => (
                  <li key={u.key} className={s.file}>
                    <Paperclip aria-hidden />
                    <span className={s.fileName}>{u.name}</span>
                    {u.error ? <span className={s.err}>{u.error}</span> : <progress className={s.progress} value={u.progress} max={1} aria-label={`Uploading ${u.name}`} />}
                    {u.error && <IconButton label="Dismiss" icon={<X />} size="sm" onClick={() => setUploads((x) => x.filter((y) => y.key !== u.key))} />}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <footer className={s.foot}>
            <div className={s.sendGroup}>
              <Button variant="primary" icon={<Send />} loading={sending} onClick={() => void send()} aria-keyshortcuts="Control+Enter" className={s.sendMain}>
                Send
              </Button>
              <Menu>
                <MenuTrigger asChild>
                  <button type="button" className={s.sendMore} aria-label="Schedule send" disabled={sending}>
                    <ChevronDown />
                  </button>
                </MenuTrigger>
                <MenuContent align="start">
                  <MenuItem icon={<Clock />} onSelect={() => void send(nextMorning(1))}>Tomorrow morning, 8:00</MenuItem>
                  <MenuItem icon={<Clock />} onSelect={() => void send(nextMorning(1, 1))}>Monday morning, 8:00</MenuItem>
                  <MenuSeparator />
                  <MenuItem icon={<Clock />} onSelect={() => setPickTime(true)}>Pick date and time</MenuItem>
                </MenuContent>
              </Menu>
            </div>
            <IconButton label="Attach files" icon={<Paperclip />} size="sm" onClick={() => fileInput.current?.click()} />
            <input ref={fileInput} type="file" multiple hidden onChange={(e) => (attach([...(e.target.files ?? [])]), (e.target.value = ""))} />
            <select className={s.sigSelect} aria-label="Signature" value={signatureId ?? ""} onChange={(e) => {
              const v = e.target.value ? Number(e.target.value) : null;
              setSignatureId(v);
              queue({ signatureId: v });
            }}>
              <option value="">No signature</option>
              {signatures.data?.items.map((x) => (
                <option key={x.id} value={x.id}>{x.name}</option>
              ))}
            </select>
            <span className={s.savedState} aria-live="polite">{saved === "saving" ? "Saving" : saved === "error" ? "Not saved" : "Saved"}</span>
            <IconButton label="Discard draft" icon={<Trash2 />} size="sm" className={s.discard} onClick={() => void discardDraft(draft.id)} />
          </footer>
          {dragging && <div className={s.drop}>Drop files to attach</div>}
        </>
      )}

      <Dialog open={confirm !== null} onOpenChange={(o) => !o && setConfirm(null)} title="Send anyway?" description={confirm?.text}>
        <div className={s.dialogActions}>
          <Button variant="ghost" onClick={() => setConfirm(null)}>Keep editing</Button>
          <Button variant="primary" onClick={() => { const c = confirm; setConfirm(null); c?.go(); }}>Send</Button>
        </div>
      </Dialog>
      {pickTime && <SchedulePicker onPick={(d) => { setPickTime(false); void send(d); }} onClose={() => setPickTime(false)} />}
    </section>
  );
}

function SchedulePicker({ onPick, onClose }: { onPick: (d: Date) => void; onClose: () => void }) {
  const local = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
  // The clock is read once when the picker opens; a minute of drift does not matter here.
  const [opened] = useState(() => ({ now: Date.now(), min: local(new Date()), initial: local(nextMorning(1)) }));
  const [value, setValue] = useState(opened.initial);
  const when = new Date(value);
  const valid = !Number.isNaN(when.getTime()) && when.getTime() > opened.now + 60_000;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Schedule send" description="The message waits in Scheduled until then. You can cancel it there.">
      <input type="datetime-local" className={s.timeInput} value={value} min={opened.min} onChange={(e) => setValue(e.target.value)} aria-label="Send at" />
      <div className={s.dialogActions}>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={!valid} onClick={() => onPick(when)}>Schedule</Button>
      </div>
    </Dialog>
  );
}
